import { cva, VariantProps } from 'class-variance-authority';
import { HTMLAttributes, ReactNode } from 'react';
import Spinner from './Spinner';

export interface ButtonCardProps extends HTMLAttributes<HTMLButtonElement> {
    children: ReactNode;
    description?: string;
    icon?: ReactNode;
    footer?: ReactNode;
    onClick: () => void;
}

const buttonCardClassNames = cva(
    'tw-bg-transparent tw-card-bordered  tw-transition-colors tw-text-left tw-flex tw-flex-col tw-gap-2  md:tw-gap-6 tw-self-stretch',
    {
        variants: {
            disabled: {
                true: 'tw-cursor-not-allowed tw-opacity-50',
                false: 'tw-cursor-pointer tw-group hover:tw-border-success'
            },
            isLoading: {
                true: 'tw-cursor-wait',
                false: ''
            }
        },
        defaultVariants: {
            disabled: false,
            isLoading: false
        }
    }
);

export default function ButtonCard({
    children,
    description,
    className,
    onClick,
    icon,
    isLoading,
    disabled,
    footer,
    ...props
}: ButtonCardProps & VariantProps<typeof buttonCardClassNames>) {
    return (
        <button
            type="button"
            onClick={!disabled && onClick}
            className={buttonCardClassNames({ className, disabled, isLoading })}
            disabled={disabled}
            {...props}
        >
            <div className="tw-flex tw-items-center tw-gap-4">
                {icon && (
                    <span className="tw-aspect-square tw-transition-colors group-hover:tw-text-success">{icon}</span>
                )}
                <span className="tw-mb-0 tw-flex-1 tw-text-xl tw-transition-colors group-hover:tw-text-success">
                    {children}
                </span>
                {isLoading && <Spinner className="tw-self-start" color="light" />}
            </div>
            {description && <p className="tw-mb-0 tw-mt-auto tw-text-accents-6">{description}</p>}
            {footer}
        </button>
    );
}
